import React, { useState } from 'react';
import { Clock, Copy, Trash2, RefreshCw } from 'lucide-react';

const EpochConverterTool = () => {
  const [mode, setMode] = useState('epochToReadable');
  const [epochInput, setEpochInput] = useState('');
  const [dateInput, setDateInput] = useState('');
  const [timeInput, setTimeInput] = useState('');
  const [timezone, setTimezone] = useState('local');
  const [output, setOutput] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  const convert = () => {
    setError('');
    setOutput(null);

    if (mode === 'epochToReadable') {
      if (!epochInput.trim()) {
        setError('Please enter an epoch timestamp');
        return;
      }

      let timestamp = parseInt(epochInput, 10);
      if (isNaN(timestamp)) {
        setError('Invalid timestamp');
        return;
      }

      // Auto-detect milliseconds vs seconds
      const isMilliseconds = timestamp > 9999999999;
      const ms = isMilliseconds ? timestamp : timestamp * 1000;
      
      const date = new Date(ms);
      if (isNaN(date.getTime())) {
        setError('Invalid timestamp');
        return;
      }

      const formats = {
        iso: date.toISOString(),
        utc: date.toUTCString(),
        local: date.toLocaleString(),
        date: date.toLocaleDateString(),
        time: date.toLocaleTimeString(),
        relative: getRelativeTime(date)
      };

      setOutput({
        timestamp: isMilliseconds ? timestamp : timestamp,
        milliseconds: ms,
        seconds: Math.floor(ms / 1000),
        formats,
        isMilliseconds
      });
    } else {
      if (!dateInput) {
        setError('Please enter a date');
        return;
      }

      const dateStr = `${dateInput}T${timeInput || '00:00'}:00`;
      const date = timezone === 'utc' 
        ? new Date(dateStr + 'Z')
        : new Date(dateStr);

      if (isNaN(date.getTime())) {
        setError('Invalid date');
        return;
      }

      setOutput({
        timestamp: Math.floor(date.getTime() / 1000),
        milliseconds: date.getTime(),
        seconds: Math.floor(date.getTime() / 1000),
        formats: {
          iso: date.toISOString(),
          utc: date.toUTCString(),
          local: date.toLocaleString()
        }
      });
    }
  };

  const getRelativeTime = (date) => {
    const now = new Date();
    const diff = now - date;
    const seconds = Math.abs(Math.floor(diff / 1000));
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    const past = diff > 0;
    const suffix = past ? 'ago' : 'from now';

    if (years > 0) return `${years} year${years > 1 ? 's' : ''} ${suffix}`;
    if (months > 0) return `${months} month${months > 1 ? 's' : ''} ${suffix}`;
    if (weeks > 0) return `${weeks} week${weeks > 1 ? 's' : ''} ${suffix}`;
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ${suffix}`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ${suffix}`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ${suffix}`;
    return 'just now';
  };

  const setNow = () => {
    const now = new Date();
    if (mode === 'epochToReadable') {
      setEpochInput(Math.floor(now.getTime() / 1000).toString());
    } else {
      setDateInput(now.toISOString().split('T')[0]);
      setTimeInput(now.toTimeString().split(' ')[0].substring(0, 5));
    }
  };

  const copyValue = (value, key) => {
    navigator.clipboard.writeText(value.toString());
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <button
          onClick={() => setMode('epochToReadable')}
          className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
            mode === 'epochToReadable' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Epoch → Readable
        </button>
        <button
          onClick={() => setMode('readableToEpoch')}
          className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
            mode === 'readableToEpoch' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Readable → Epoch
        </button>
      </div>

      {mode === 'epochToReadable' ? (
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-gray-300">Epoch Timestamp</label>
            <button onClick={setNow} className="text-xs text-blue-400 hover:text-blue-300">
              Use Current Time
            </button>
          </div>
          <input
            type="text"
            value={epochInput}
            onChange={(e) => setEpochInput(e.target.value)}
            placeholder="1234567890 or 1234567890000"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white font-mono placeholder-gray-500 focus:border-blue-500"
            onKeyDown={(e) => e.key === 'Enter' && convert()}
          />
          <p className="text-xs text-gray-500 mt-1">Supports both seconds and milliseconds</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Date</label>
              <input
                type="date"
                value={dateInput}
                onChange={(e) => setDateInput(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Time</label>
              <input
                type="time"
                value={timeInput}
                onChange={(e) => setTimeInput(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Timezone</label>
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
              >
                <option value="local">Local Time</option>
                <option value="utc">UTC</option>
              </select>
            </div>
          </div>
          <button onClick={setNow} className="text-xs text-blue-400 hover:text-blue-300">
            Use Current Time
          </button>
        </div>
      )}

      <button
        onClick={convert}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
      >
        <Clock size={18} />
        Convert
      </button>

      {output && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg">
              <div className="text-xs text-gray-500 mb-1">Unix Timestamp (seconds)</div>
              <div className="flex items-center justify-between">
                <code className="text-xl font-mono text-green-400">{output.seconds}</code>
                <button
                  onClick={() => copyValue(output.seconds, 'seconds')}
                  className={`p-1.5 rounded ${copied === 'seconds' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <div className="text-xs text-gray-500 mb-1">Milliseconds</div>
              <div className="flex items-center justify-between">
                <code className="text-xl font-mono text-blue-400">{output.milliseconds}</code>
                <button
                  onClick={() => copyValue(output.milliseconds, 'ms')}
                  className={`p-1.5 rounded ${copied === 'ms' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>
          </div>

          <div className="p-4 bg-gray-800 rounded-lg space-y-2">
            {Object.entries(output.formats).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <div>
                  <span className="text-xs text-gray-500 uppercase">{key}: </span>
                  <span className="text-gray-300">{value}</span>
                </div>
                <button
                  onClick={() => copyValue(value, key)}
                  className={`p-1 rounded ${copied === key ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                >
                  <Copy size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Current Time</h3>
        <div className="text-sm text-gray-400">
          <span className="text-green-400 font-mono">{Math.floor(Date.now() / 1000)}</span> (seconds)
          {' • '}
          <span className="text-blue-400 font-mono">{Date.now()}</span> (milliseconds)
        </div>
      </div>

      <button
        onClick={() => { setEpochInput(''); setDateInput(''); setTimeInput(''); setOutput(null); setError(''); }}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear
      </button>
    </div>
  );
};

export default EpochConverterTool;
